<script setup lang="ts">
import { ref, onMounted, watch } from "vue";

const propsData = withDefaults(
  defineProps<{
    label?: string;
    sublabel?: string;
    count?: string | number;
    icon?: any;
    color?: string;
    gradient?: string;
  }>(),
  {
    label: "Metric",
    sublabel: "",
    count: 0,
    color: "primary",
    gradient: "linear-gradient(135deg, #1e88e5 0%, #1565c0 100%)",
  }
);

const displayCount = ref<string | number>(0);

const parseNumber = (v: any) => {
  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const num = Number(v.toString().replace(/[^0-9.-]+/g, ""));
    return isNaN(num) ? null : num;
  }
  return null;
};

const animateCount = (to: number, duration = 650) => {
  const start = performance.now();
  const from = 0;
  const step = (ts: number) => {
    const t = Math.min(1, (ts - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    displayCount.value = Math.round(from + (to - from) * eased);
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
};

onMounted(() => {
  const n = parseNumber(propsData.count);
  if (n !== null) animateCount(n, 650);
  else displayCount.value = propsData.count ?? "";
});

watch(
  () => propsData.count,
  (nv) => {
    const n = parseNumber(nv);
    if (n !== null) animateCount(n, 650);
    else displayCount.value = nv;
  }
);
</script>

<template>
  <v-card
    elevation="0"
    class="stats-metric-card h-100 pa-3 pa-sm-4 d-flex align-center position-relative overflow-hidden"
  >
    <div class="d-flex align-center flex-grow-1" style="gap: 10px;">
      <!-- Icon Avatar with Gradient & 3D Shadow -->
      <v-avatar size="44" class="metric-avatar flex-shrink-0" :style="{ background: propsData.gradient }">
        <component :is="propsData.icon" stroke-width="2" size="22" class="text-white" />
      </v-avatar>

      <!-- Label & Count -->
      <div class="metric-text-group min-width-0">
        <div class="metric-label font-weight-bold text-medium-emphasis text-uppercase text-truncate">
          {{ propsData.label }}
        </div>
        <div class="count-value font-weight-black mt-0">
          {{ displayCount }}
        </div>
        <div v-if="propsData.sublabel" class="metric-sublabel text-medium-emphasis d-none d-sm-block text-truncate">
          {{ propsData.sublabel }}
        </div>
      </div>
    </div>

    <!-- Decorative background glow -->
    <div class="card-glow" :style="{ background: propsData.gradient }"></div>
  </v-card>
</template>

<style scoped>
.stats-metric-card {
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(14px);
  border: 1px solid rgba(226, 232, 240, 0.85);
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  transition: all 0.25s ease;
}

.stats-metric-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.1);
  border-color: rgba(var(--v-theme-primary), 0.3);
}

.metric-avatar {
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.4);
}

.metric-label {
  font-size: 0.75rem;
  letter-spacing: 0.4px;
  line-height: 1.2;
}

.count-value {
  color: #0f172a;
  font-size: 1.5rem;
  line-height: 1.15;
  letter-spacing: -0.5px;
}

.metric-sublabel {
  font-size: 0.7rem;
  line-height: 1.1;
}

.card-glow {
  position: absolute;
  top: -24px;
  right: -24px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  opacity: 0.12;
  filter: blur(16px);
  pointer-events: none;
}

@media (max-width: 600px) {
  .stats-metric-card {
    padding: 10px !important;
  }
  .metric-avatar {
    width: 36px !important;
    height: 36px !important;
  }
  .metric-avatar :deep(svg) {
    width: 18px !important;
    height: 18px !important;
  }
  .metric-label {
    font-size: 0.68rem !important;
  }
  .count-value {
    font-size: 1.25rem !important;
  }
}
</style>
